import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MetaballsBackground from '../shared/MetaballsBackground';
import GradientBackground from '../shared/GradientBackground';
import BudmenLogo from '../shared/BudmenLogo';

// ═══════════════════════════════════════════════════════════════════
// HERO SECTION - Immersive introduction
// ═══════════════════════════════════════════════════════════════════

const HeroSection = ({ onScrollToPortal }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // Impossible questions from the original homepage
  const impossibleQuestions = [
    "Can you 3D scan a 15ft statue?",
    "Can you make a cyborg sculpture?",
    "Can you create an infinite drawing machine?",
    "Can you build a robot that paints?",
    "Can you turn sound into sculpture?",
    "Can you print with light?"
  ];

  // Rotate through questions
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % impossibleQuestions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [impossibleQuestions.length]);

  return (
    <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Background layers */}
      <MetaballsBackground />
      <GradientBackground intensity="normal" />

      {/* Scattered dots pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Budmen Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-8"
        >
          <BudmenLogo className="mx-auto" height="50" fill="white" />
        </motion.div>

        {/* Main heading with animation */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-5xl md:text-7xl font-bold text-white mb-6"
          style={{ fontFamily: 'futura-pt, sans-serif' }}
        >
          What's Your{' '}
          <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
            Impossible?
          </span>
        </motion.h1>

        {/* Rotating questions */}
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
          className="text-xl md:text-2xl text-white/80 mb-8 italic"
          style={{ fontFamily: 'futura-pt, sans-serif' }}
        >
          "{impossibleQuestions[currentQuestion]}"
        </motion.div>

        {/* Subheading */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-lg md:text-xl text-white/60 mb-12 max-w-2xl mx-auto"
        >
          Whether you need a speaker, a workshop, or help bringing an idea to life—let's explore together
        </motion.p>

        {/* CTA Button */}
        <motion.button
          onClick={onScrollToPortal}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          className="px-8 py-4 bg-white text-gray-900 font-semibold rounded-full text-lg shadow-lg hover:shadow-xl transition-all duration-300"
        >
          Let's Get Started
        </motion.button>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{
          opacity: { delay: 1, duration: 0.5 },
          y: { repeat: Infinity, duration: 1.5, ease: "easeInOut" }
        }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <svg
          className="w-6 h-10 text-white/40"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 40"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="1" y="1" width="22" height="28" rx="11" strokeWidth="2" />
          <circle cx="12" cy="10" r="2" fill="currentColor" />
        </svg>
      </motion.div>
    </section>
  );
};

export default HeroSection;